/* eslint-disable no-underscore-dangle */
import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import toast from "react-hot-toast";
import { queries } from "../services/queries";
import { Emergencies, Operators } from "../services";
import { isErrorMessage } from "../util/helpers";

export function useEmergencies() {
  return useQuery({
    ...queries.emergencies.all,
    queryFn: () => Emergencies.getAll(),
    staleTime: 60 * 1000, // 1 Min
  });
}

export function useEmergencyDetail(id: string) {
  return useQuery({
    ...queries.emergencies.detail(id),
  });
}

export function useEmergenciesList(filter: Filters) {
  return useQuery({
    ...queries.emergencies.list(filter),
    staleTime: 60 * 1000,
  });
}

export function useSearchEmergencies(
  filters: Filters,
  query: string,
  limit = 15
) {
  return useQuery({
    ...queries.emergencies.list(filters)._ctx.search(query, limit),
    enabled: Boolean(query),
  });
}
export function useAddEmergency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: Emergencies.addEmergency,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queries.emergencies._def,
      });
    },
    onError: (error: AxiosError) => {
      const errorMessage = error.response?.data;

      if (typeof errorMessage === "string") {
        toast.error(errorMessage);
      } else if (isErrorMessage(errorMessage)) {
        const errorTitle = errorMessage.title;
        const errorKeys = Object.keys(errorMessage.errors || {});
        if (errorKeys.length > 0 && errorMessage.errors) {
          const errorFieldMessage = `${errorKeys[0]}: ${
            errorMessage.errors[errorKeys[0]][0]
          }`;
          toast.error(errorFieldMessage);
        } else {
          toast.error(errorTitle);
        }
      } else {
        toast.error("Something went wrong");
      }
    },
    meta: {
      useError: true,
    },
  });
}

export function useEditEmergency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: Emergencies.updateEmergencies,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queries.emergencies._def,
      });
    },
    onError: (error: AxiosError) => {
      const errorMessage = error.response?.data;

      if (typeof errorMessage === "string") {
        toast.error(errorMessage);
      } else if (isErrorMessage(errorMessage)) {
        const errorTitle = errorMessage.title;
        const errorKeys = Object.keys(errorMessage.errors || {});
        if (errorKeys.length > 0 && errorMessage.errors) {
          const errorFieldMessage = `${errorKeys[0]}: ${
            errorMessage.errors[errorKeys[0]][0]
          }`;
          toast.error(errorFieldMessage);
        } else {
          toast.error(errorTitle);
        }
      } else {
        toast.error("Something went wrong");
      }
    },
    meta: {
      useError: true,
    },
  });
}

export function useDeleteEmergency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: Emergencies.deleteEmergencyById,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queries.emergencies._def,
      });
      queryClient.invalidateQueries({
        queryKey: ["operators"],
      });
    },
  });
}
